import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';

export const SEOHead = ({
  title,
  description,
  canonical,
  keywords,
  image,
  schemaEndpoint,
  schema,
  robots,
  ogType = 'website',      // e.g. 'website' | 'product' | 'article'
  twitterCard = 'summary_large_image',
}) => {
  const [ld, setLd] = useState(schema || null);

  useEffect(() => {
    if (schema) { setLd(schema); return; }
    if (!schemaEndpoint) { setLd(null); return; }
    let cancelled = false;
    api.get(schemaEndpoint)
      .then(r => { if (!cancelled) setLd(r.data); })
      .catch(() => { if (!cancelled) setLd(null); });
    return () => { cancelled = true; };
  }, [schemaEndpoint, schema]);

  // Canonical must NEVER include query params (utm/filters/etc.) — default to
  // the param-stripped path so parameterized/tracking URLs can't self-canonicalize.
  const canon = canonical || (typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : '');
  const trimDesc = description ? description.slice(0, 170) : '';
  // Single source for the social image. Falls back to the site default so every
  // page emits exactly ONE og:image / twitter:image (the static default was
  // removed from index.html to avoid a duplicate/conflicting og:image).
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://indusfact.com';
  const ogImage = image || `${origin}/og-default.jpg`;

  return (
    <Helmet>
      {title && <title>{title}</title>}
      {trimDesc && <meta name='description' content={trimDesc} />}
      {keywords && <meta name='keywords' content={keywords} />}
      {robots && <meta name='robots' content={robots} />}
      {canon && <link rel='canonical' href={canon} />}

      {/* Open Graph — for LinkedIn, Facebook, WhatsApp, Telegram previews */}
      {title && <meta property='og:title' content={title} />}
      {trimDesc && <meta property='og:description' content={trimDesc} />}
      <meta property='og:type' content={ogType} />
      {canon && <meta property='og:url' content={canon} />}
      <meta property='og:image' content={ogImage} />
      <meta property='og:image:alt' content={title || 'IndusFact B2B Solutions'} />
      <meta property='og:site_name' content='IndusFact B2B Solutions' />
      <meta property='og:locale' content='en_IN' />

      {/* Twitter / X card */}
      <meta name='twitter:card' content={twitterCard} />
      <meta name='twitter:site' content='@indusfact' />
      {title && <meta name='twitter:title' content={title} />}
      {trimDesc && <meta name='twitter:description' content={trimDesc} />}
      <meta name='twitter:image' content={ogImage} />

      {ld && (
        <script type='application/ld+json'>{JSON.stringify(ld)}</script>
      )}
    </Helmet>
  );
};

export default SEOHead;
